// src/services/aeo/gbpProfile.ts
//
// Each outlet's live Google Business Profile, as staff recorded it
// (ourteam-backend#5277, FE #4010).
//
// Oura has no Google Business Profile API (the owner's decision, 7 Oct 2026),
// so the GBP Pack skill cannot read the profile itself. Staff type the fields
// in and upload a "before" screenshot per section, and the skill reads that
// record in place of someone opening the profile in Chrome.
//
//   getGbpProfiles(businessId)                     GET    /businesses/{id}/gbp-profiles
//   saveGbpFields(businessId, outlet, fields)      PUT    /businesses/{id}/gbp-profiles/{outlet}
//   readGbpFromGoogle(businessId, outlet)          POST   /businesses/{id}/gbp-profiles/{outlet}/read-google
//   addGbpScreenshot(businessId, outlet, …)        POST   /businesses/{id}/gbp-profiles/{outlet}/screenshots
//   removeGbpScreenshot(businessId, outlet, id)    DELETE /businesses/{id}/gbp-profiles/{outlet}/screenshots/{id}
//
// Every write answers the WHOLE payload, so a caller renders the server's own
// answer rather than what it hoped it sent. Nothing here throws: a refusal
// comes back as `{ok: false, code, message}` carrying the server's sentence,
// which is the one the screen shows.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;

// No fixtures and no flag. A sample Google profile is a record of what a real
// client's profile says, and inventing one is exactly the mistake the house rule
// is about. The routes are served in every environment (ourteam-backend#5362).

/** `text` is up to `limit` CHARACTERS; `list` is up to `limit` LINES. */
export type GbpFieldKind = "text" | "list" | "number" | "bool";

export interface GbpField {
    key: string;
    label: string;
    kind: GbpFieldKind;
    /** Null on `number` and `bool`, which have no length. */
    limit: number | null;
}

export interface GbpShot {
    id: string;
    label: string;
    caption: string | null;
    name: string | null;
    /**
     * PRESIGNED FOR ONE HOUR. Never cached across a visit: read the payload
     * again instead, which every call here answers with.
     */
    url: string | null;
    addedAt: string | null;
    addedBy: string | null;
}

/** A field's stored value. A key absent from `fields` was not recorded. */
export type GbpValue = string | string[] | number | boolean;

export interface GbpOutlet {
    /** The slug. "" for a business with one outlet; the routes take "-" for it. */
    outlet: string;
    name: string;
    confirmed: boolean;
    fields: Record<string, GbpValue>;
    screenshots: GbpShot[];
    updatedBy: string | null;
    updatedAt: string | null;
}

export interface GbpProfiles {
    outlets: GbpOutlet[];
    /** The form, in the server's order. The screen draws from this, never from a list of its own. */
    fieldList: GbpField[];
    shotLabels: string[];
}

export type GbpResult =
    | { ok: true; data: GbpProfiles }
    /** `code` is the server's refusal slug; `message` is its sentence, which the screen shows. */
    | { ok: false; code: string; message: string };

/** At most this many screenshots per outlet, as the server counts them. */
export const GBP_MAX_SHOTS = 20;
/** The server truncates a caption to this. */
export const GBP_CAPTION_MAX = 200;
export const GBP_SHOT_TYPES = ["image/png", "image/jpeg", "image/webp"];
/** 20 MB, the server's `upload_too_large`. */
export const GBP_SHOT_MAX_BYTES = 20 * 1024 * 1024;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

/**
 * Auth WITHOUT a Content-Type, for the screenshot upload.
 *
 * axios sends exactly the Content-Type it is handed, so a FormData body with
 * `application/json` on it never gets the `multipart/form-data; boundary=…`
 * that makes it parseable, and the server reports a missing field for what is
 * really a missing boundary.
 */
function multipartAuthHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The slug in a URL: a business with one outlet has an empty slug, and the routes spell that "-". */
const seg = (outlet: string) => encodeURIComponent(outlet || "-");

/**
 * The server's own refusal, or ours when no response reached us.
 *
 * The code is `errors[0]` and the sentence is `message`, the envelope every
 * AEO route uses. A request that never got an answer has no server sentence,
 * so it says so rather than borrowing one.
 */
function refusal(err: unknown, where: string): GbpResult {
    const res = (err as { response?: { status?: number; data?: { message?: string; errors?: string[] } } }).response;
    if (!res) return { ok: false, code: "network", message: "The server did not answer. Check your connection and try again." };
    const code = res.data?.errors?.[0] ?? "error";
    const message = res.data?.message ?? "That did not go through. Try again.";
    // The refusals are the operator's to read and act on; only a shape nobody
    // predicted is worth an incident.
    if (!res.data?.message) catchErrorLog(`[oura] gbp ${where} failed (${res.status ?? "no status"})`);
    return { ok: false, code, message };
}

const unwrap = (body: unknown): GbpProfiles => ((body as { data?: GbpProfiles })?.data ?? body) as GbpProfiles;

/**
 * A 2xx whose body is the payload, or a refusal when it is not.
 *
 * A 200 that is not this route's JSON (an HTML page from a proxy, or the dev
 * server's own index.html when no backend URL is set) would otherwise reach the
 * card as a string and take the whole Client tab down on `outlets.filter`.
 */
function answer(body: unknown, where: string): GbpResult {
    const data = unwrap(body);
    if (data && Array.isArray(data.outlets) && Array.isArray(data.fieldList) && Array.isArray(data.shotLabels)) {
        return { ok: true, data };
    }
    catchErrorLog(`[oura] gbp ${where} answered an unexpected shape`);
    return { ok: false, code: "bad_shape", message: "The server's answer could not be read. Try again." };
}

export async function getGbpProfiles(businessId: string): Promise<GbpResult> {
    // A local fixtures run has no backend URL: the request would go to the dev
    // server itself. Say so on the card rather than read its index.html.
    if (!apiURL) return { ok: false, code: "no_backend", message: "No backend is set for this preview, so the profile cannot be read." };
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/gbp-profiles`, { headers: authHeaders() });
        return answer(res.data, "read");
    } catch (err) {
        return refusal(err, "read");
    }
}

/**
 * Save one outlet's fields.
 *
 * REPLACES them, so send the whole form. A blank value is dropped by the
 * server and reads as "not recorded" afterwards — it is never stored as an
 * empty answer, so leaving a field out and clearing it are the same act.
 */
export async function saveGbpFields(businessId: string, outlet: string, fields: Record<string, GbpValue>): Promise<GbpResult> {
    try {
        const res = await axios.put(
            `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/gbp-profiles/${seg(outlet)}`,
            { fields },
            { headers: authHeaders() },
        );
        return answer(res.data, "save");
    } catch (err) {
        return refusal(err, "save");
    }
}

/**
 * The server reads the outlet's public Google Maps listing through Bright Data
 * and answers when it is done: 12 to 50 seconds, in one request. A bound well
 * past that, so a slow read is waited for and a dead one is not.
 */
const READ_GOOGLE_TIMEOUT_MS = 120_000;

/**
 * Fill one outlet from its public Google Maps listing
 * (ourteam-backend `oura_gbp.read_from_google`).
 *
 * Only the fields a public listing shows are written, over what was saved:
 * what staff alone know (whether the client manages it, products, posts,
 * attributes, photos, notes) is kept as it was. Answers the whole payload, like
 * every write here. `gbp_google_unread` (424) means no listing was clearly this
 * outlet's; its sentence is the server's own and the screen shows it.
 */
export async function readGbpFromGoogle(businessId: string, outlet: string): Promise<GbpResult> {
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/gbp-profiles/${seg(outlet)}/read-google`,
            {},
            { headers: authHeaders(), timeout: READ_GOOGLE_TIMEOUT_MS },
        );
        return answer(res.data, "read from Google");
    } catch (err) {
        return refusal(err, "read from Google");
    }
}

export async function addGbpScreenshot(
    businessId: string,
    outlet: string,
    input: { file: File; label: string; caption?: string },
): Promise<GbpResult> {
    const form = new FormData();
    form.append("file", input.file);
    form.append("label", input.label);
    if (input.caption?.trim()) form.append("caption", input.caption.trim());
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/gbp-profiles/${seg(outlet)}/screenshots`,
            form,
            { headers: multipartAuthHeaders() },
        );
        return answer(res.data, "upload");
    } catch (err) {
        return refusal(err, "upload");
    }
}

export async function removeGbpScreenshot(businessId: string, outlet: string, shotId: string): Promise<GbpResult> {
    try {
        const res = await axios.delete(
            `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/gbp-profiles/${seg(outlet)}/screenshots/${encodeURIComponent(shotId)}`,
            { headers: authHeaders() },
        );
        return answer(res.data, "remove");
    } catch (err) {
        return refusal(err, "remove");
    }
}
